import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useExternalQuoteRequestsControllerFindOne } from "@semo/api-contracts";
import { QuoteReview, QuoteHeader } from "@semo/shared-ui";
import { Skeleton } from "@semo/shared-ui";
import {
  AlertCircle,
  Calendar,
  CalendarPlus,
  ArrowLeft,
  User,
  Phone,
  MessageSquare,
  Building2,
} from "@semo/icons";
import { formatDateForDisplay } from "@semo/shared-ui";
import { getCategoryStyles } from "@semo/shared-ui";
import { QuoteRequestDetailDto } from "@semo/shared-ui";
import { cn } from "@semo/shared-ui";
import { SidebarInfoCard } from "@semo/shared-ui";
import { ContractDocumentsSection } from "../components/ContractDocumentsSection";
import { MallOrderTrackingSection } from "../components/MallOrderTrackingSection";
import { isQuoteRequestClosed } from "../../domain/quoteRequestClosed";
import { MyQuoteWithdrawal } from "@/features/quotes/presentation/components/WithdrawQuoteButton";

interface RespondedProjectDetailPageProps {
  requestId?: string;
  onBack?: () => void;
  onChatClick?: (quoteRequestId: string) => void;
  /**
   * Whether to hide the buyer information sidebar.
   * Useful when viewing projects that the supplier hasn't responded to yet.
   */
  hideBuyerInfo?: boolean;
  /**
   * 내부 액션 버튼(예: 발주처 카드의 "1:1 채팅하기") 노출 여부.
   * 2026-07-31: c-market /embed/quote-detail 진입 시 true — 발주처 정보는 노출하되
   *   상세 액션은 숨겨서 임베드 사용자를 외부 흐름에 남긴다.
   */
  hideActions?: boolean;
}

export const RespondedProjectDetailPage: React.FC<
  RespondedProjectDetailPageProps
> = ({
  requestId: propRequestId,
  onBack,
  onChatClick,
  hideBuyerInfo = false,
  hideActions = false,
}) => {
  const { requestId: paramRequestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();

  const requestId = propRequestId || paramRequestId;
  const handleBack = onBack || (() => navigate(-1));

  const {
    data: response,
    isLoading,
    error,
  } = useExternalQuoteRequestsControllerFindOne(requestId || "", {
    query: { enabled: !!requestId },
  });

  const quoteRequest = response?.data;

  const buyerOrganizationName = (quoteRequest?.organizationName ?? "").trim();
  const buyerManagerName =
    (quoteRequest?.managerName as unknown as string) || "";

  const handleChatClick = () => {
    if (quoteRequest?.id && onChatClick) {
      onChatClick(quoteRequest.id);
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div
        className={cn(
          "bg-slate-50 p-6 sm:p-10 md:p-12",
          propRequestId ? "h-full" : "min-h-screen",
        )}
      >
        <div
          className={cn(
            "mx-auto space-y-8",
            hideBuyerInfo ? "max-w-4xl" : "max-w-5xl",
          )}
        >
          <div className="flex items-center gap-4">
            <Skeleton className="h-12 w-12 rounded-md" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <Skeleton className="h-[400px] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  // Error State
  if (error || !quoteRequest) {
    return (
      <div
        className={cn(
          "bg-slate-50 p-6 sm:p-10 md:p-12 flex items-center justify-center",
          propRequestId ? "h-full" : "min-h-screen",
        )}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <AlertCircle className="w-12 h-12 text-red-500" />
          <p className="text-slate-600 ">
            견적 요청 정보를 찾을 수 없거나 불러오는 중 오류가 발생했습니다.
          </p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
          >
            뒤로 가기
          </button>
        </div>
      </div>
    );
  }

  const categoryStyles = getCategoryStyles(quoteRequest.categoryType);

  return (
    <div
      className={cn(
        "bg-slate-50 overflow-y-auto",
        propRequestId ? "h-full" : "min-h-screen",
      )}
    >
      <div
        className={cn(
          "mx-auto p-6 sm:p-8 md:p-10 lg:p-12",
          hideBuyerInfo ? "max-w-5xl" : "max-w-[1400px]",
        )}
      >
        <div
          className={cn(
            "grid grid-cols-1 gap-8",
            hideBuyerInfo ? "lg:grid-cols-1" : "lg:grid-cols-[1fr_320px]",
          )}
        >
          {/* Left Column */}
          <div className="space-y-8">
            {/* Navigation - Only show if not in modal mode */}
            {!propRequestId && (
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                목록으로 돌아가기
              </button>
            )}

            {/* Header Section */}
            <QuoteHeader
              title={quoteRequest.requestNumber}
              badges={[
                {
                  label: categoryStyles.label,
                  variant: "secondary",
                  className: `${categoryStyles.bg} ${categoryStyles.text} ${categoryStyles.border} border`,
                },
                // 공급사 API 는 마감을 `isClosed` 로 따로 준다 — status 만 보면 마감일이 지난
                // 공고도 «진행중», 계약 진행 중인 공고는 «마감됨» 으로 보였다(V-10·A3-09).
                {
                  label: isQuoteRequestClosed(quoteRequest)
                    ? "마감"
                    : quoteRequest.status === "CONTRACT_IN_PROGRESS"
                      ? "계약 진행중"
                      : "진행중",
                  variant: isQuoteRequestClosed(quoteRequest)
                    ? "secondary"
                    : "primary",
                },
              ]}
              projectName={quoteRequest.projectName}
              metadata={[
                {
                  icon: Calendar,
                  label: "마감",
                  value: formatDateForDisplay(quoteRequest.deadline),
                },
                {
                  icon: CalendarPlus,
                  label: "등록",
                  value: formatDateForDisplay(
                    quoteRequest.createdAt as unknown as string,
                  ),
                },
              ]}
              mode="detail"
            />

            {/* 내 견적 철회(V-13) — 마감 전·계약 전이면 [견적 철회], 철회했으면 «철회됨».
                아직 견적을 내지 않은 공고면 아무것도 그리지 않는다. */}
            <MyQuoteWithdrawal
              quoteRequest={quoteRequest}
              readOnly={hideActions}
              className="flex justify-end"
            />

            {/* Content Section - QuoteReview */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-1">
              <QuoteReview
                dataSource={{
                  source: "api",
                  apiData: quoteRequest as unknown as QuoteRequestDetailDto,
                }}
                categoryType={quoteRequest.categoryType}
                mode="detail"
              />
            </div>

            {/* 낙찰된 계약이 있을 때만 나타난다 — 없으면 아무것도 그리지 않는다. */}
            <ContractDocumentsSection requestId={requestId ?? ""} />

            {/* 몰에서 올라온 공고일 때만. 일반 공고에는 아무것도 안 그린다.
                주문 축은 UUID 로만 찾으므로 응답의 id 를 쓴다(경로 인자는 공고번호일 수 있다). */}
            <MallOrderTrackingSection quoteRequestId={quoteRequest.id} />
          </div>

          {/* Right Column: Buyer Info (Conditional) */}
          {!hideBuyerInfo && (
            <div className="mt-0 lg:mt-24">
              {/* 발주기관명은 백엔드가 공고 공개 여부(isPublic)로 가려 내린다 — 있으면 기관을
                  머리에 세우고 담당자는 아래 줄로. 비공개 공고·기관명 없는 구매자는 담당자만. */}
              <SidebarInfoCard
                title="구매자 정보"
                name={buyerOrganizationName || buyerManagerName}
                subText={buyerOrganizationName ? "발주기관" : "구매 담당자"}
                avatarIcon={buyerOrganizationName ? Building2 : User}
                infoRows={[
                  ...(buyerOrganizationName
                    ? [{ icon: Building2, value: buyerOrganizationName }]
                    : []),
                  {
                    icon: User,
                    value: buyerManagerName,
                  },
                  {
                    icon: Phone,
                    value: quoteRequest.managerContact || "",
                  },
                ]}
                actionButton={
                  hideActions
                    ? undefined
                    : {
                        label: "1:1 채팅하기",
                        icon: MessageSquare,
                        onClick: handleChatClick,
                      }
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
